import { Capacitor } from "@capacitor/core";
import { IAP, COIN_PACKS } from "../game/constants";
import { useGame } from "../game/store";
import { verifyIap } from "./cloud";
import { syncGameCenter } from "./gameCenter";
import { reportError } from "./crash";

export type IapResult = { ok: boolean; reason?: string };

let initPromise: Promise<boolean> | null = null;

/**
 * Best-effort App Store Server API check. Must never block delivery:
 * App Review / sandbox txns often 401/404 on Get Transaction Info, and
 * that used to skip grantRewardedCoins (Guideline 2.1(b) — coins stuck).
 * When Apple confirms the txn, the server credits once and we take that save.
 */
async function verifyTransaction(
  transactionId: string | undefined,
  productId: string,
): Promise<void> {
  if (!transactionId) return;
  const g = useGame.getState();
  const result = await verifyIap({
    transactionId,
    productId,
    playerId: g.leaderboardId(),
    appleUserId: g.appleUserId,
  });
  if (result?.credited && result.player) {
    g.applyCloudSave(result.player);
  }
}

const grantedTx = new Set<string>();
let deliveredSeq = 0;
let lastOrderedId: string | null = null;

function notifyDelivered() {
  deliveredSeq += 1;
}

function waitForDelivery(before: number, ms: number) {
  return new Promise<void>((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (deliveredSeq > before || Date.now() - start >= ms) resolve();
      else window.setTimeout(tick, 40);
    };
    tick();
  });
}

function productIdsOf(transaction: {
  products?: { id: string }[];
}): string[] {
  const ids = (transaction.products ?? [])
    .map((p) => p.id)
    .filter(Boolean);
  if (ids.length) return [...new Set(ids)];
  return lastOrderedId ? [lastOrderedId] : [];
}

function deliver(productId: string) {
  if (productId === IAP.REMOVE_ADS) {
    useGame.getState().applyRemoveAds();
  }
  if (productId === IAP.STARTER_PACK) {
    useGame.getState().applyStarterPack();
    void syncGameCenter({ runScore: 0 });
  }
  const coins = COIN_PACKS[productId];
  if (coins) {
    useGame.getState().grantRewardedCoins(coins);
  }
}

function syncOwned(
  products: { id: string; owned?: boolean }[],
) {
  for (const p of products) {
    if (!p.owned) continue;
    // Consumable coin packs must never auto-grant on launch.
    if (p.id in COIN_PACKS) continue;
    deliver(p.id);
  }
}

/** Init StoreKit once on native. Safe to call repeatedly. */
export function initIap(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return Promise.resolve(false);
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const { store, ProductType, Platform } = await import(
        "capacitor-plugin-cdv-purchase"
      );

      store.register([
        {
          id: IAP.REMOVE_ADS,
          type: ProductType.NON_CONSUMABLE,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: IAP.STARTER_PACK,
          type: ProductType.NON_CONSUMABLE,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: IAP.COINS_SMALL,
          type: ProductType.CONSUMABLE,
          platform: Platform.APPLE_APPSTORE,
        },
        {
          id: IAP.COINS_LARGE,
          type: ProductType.CONSUMABLE,
          platform: Platform.APPLE_APPSTORE,
        },
      ]);

      store.when().approved((transaction) => {
        void (async () => {
          const ids = productIdsOf(transaction);
          const key = `${transaction.transactionId || ""}:${ids.join(",")}`;
          if (!grantedTx.has(key)) {
            grantedTx.add(key);
            for (const id of ids) deliver(id);
            if (ids.length) notifyDelivered();
          }
          await transaction.finish();
          for (const id of ids) {
            void verifyTransaction(transaction.transactionId, id);
          }
        })();
      });

      await store.initialize([Platform.APPLE_APPSTORE]);
      await store.update();
      if (!store.isReady) {
        await new Promise<void>((resolve) => {
          store.ready(() => resolve());
        });
      }
      store.when().productUpdated(() => rememberPrices(store, Platform));
      rememberPrices(store, Platform);
      syncOwned(store.products);
      return true;
    } catch (e) {
      console.warn("[iap] init failed", e);
      reportError(e, "iap.init");
      initPromise = null;
      return false;
    }
  })();

  return initPromise;
}

async function purchase(
  productId: string,
  onSuccess: () => void,
): Promise<IapResult> {
  if (!Capacitor.isNativePlatform()) {
    onSuccess();
    return { ok: true, reason: "web-mock" };
  }

  const ok = await initIap();
  if (!ok) {
    if (import.meta.env.VITE_IAP_MOCK === "1") {
      onSuccess();
      return { ok: true, reason: "native-mock" };
    }
    return { ok: false, reason: "store_unavailable" };
  }

  try {
    const { store, Platform } = await import("capacitor-plugin-cdv-purchase");
    const product = store.get(productId, Platform.APPLE_APPSTORE);
    let offer = product?.getOffer();
    if (!offer) {
      await store.update();
      offer = store.get(productId, Platform.APPLE_APPSTORE)?.getOffer();
    }
    if (!offer) {
      return { ok: false, reason: "product_unavailable" };
    }
    lastOrderedId = productId;
    const before = deliveredSeq;
    const err = await offer.order();
    if (err) {
      return { ok: false, reason: err.message || String(err.code) };
    }
    await waitForDelivery(before, 8000);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
}

export async function purchaseRemoveAds(): Promise<IapResult> {
  return purchase(IAP.REMOVE_ADS, () => useGame.getState().applyRemoveAds());
}

export async function purchaseStarterPack(): Promise<IapResult> {
  return purchase(IAP.STARTER_PACK, () => {
    useGame.getState().applyStarterPack();
    void syncGameCenter({ runScore: 0 });
  });
}

export async function purchaseCoinPack(
  productId: typeof IAP.COINS_SMALL | typeof IAP.COINS_LARGE,
): Promise<IapResult> {
  const n = COIN_PACKS[productId] ?? 0;
  return purchase(productId, () => useGame.getState().grantRewardedCoins(n));
}

/** Restore non-consumables (App Review expects this). */
export async function restorePurchases(): Promise<IapResult> {
  if (!Capacitor.isNativePlatform()) {
    return { ok: true, reason: "web-noop" };
  }
  const ok = await initIap();
  if (!ok) return { ok: false, reason: "store_unavailable" };

  try {
    const { store } = await import("capacitor-plugin-cdv-purchase");
    const err = await store.restorePurchases();
    if (err) {
      return { ok: false, reason: err.message || String(err.code) };
    }
    syncOwned(store.products);
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: String(e) };
  }
}

export function iapProductIds() {
  return [
    IAP.REMOVE_ADS,
    IAP.STARTER_PACK,
    IAP.COINS_SMALL,
    IAP.COINS_LARGE,
  ];
}

const PRICE_FALLBACK: Record<string, string> = {
  [IAP.REMOVE_ADS]: "$2.99",
  [IAP.STARTER_PACK]: "$4.99",
  [IAP.COINS_SMALL]: "$0.99",
  [IAP.COINS_LARGE]: "$2.99",
};

let priceCache: Record<string, string> = { ...PRICE_FALLBACK };
const priceListeners = new Set<() => void>();

function rememberPrices(
  store: { get: (id: string, platform: unknown) => { pricing?: { price?: string } } | undefined },
  Platform: { APPLE_APPSTORE: unknown },
) {
  for (const id of Object.keys(PRICE_FALLBACK)) {
    const label = store.get(id, Platform.APPLE_APPSTORE)?.pricing?.price;
    if (label) priceCache[id] = label;
  }
  priceListeners.forEach((fn) => fn());
}

export function iapPriceLabel(productId: string): string {
  return priceCache[productId] ?? PRICE_FALLBACK[productId] ?? "";
}

export function subscribeIapPrices(cb: () => void): () => void {
  priceListeners.add(cb);
  return () => priceListeners.delete(cb);
}
